/**
 * PartDetailPanel.jsx — the diagnostic payoff of the exploded view.
 *
 * Given a selected node id and the assembly tree, shows the part's identity and
 * its path through the assembly (geometry truth, always available offline),
 * then knowledge-graph enrichment fetched from the backend: matched KG
 * entities, specifications, owning systems, failure modes and procedures.
 *
 * GRACEFUL DEGRADATION IS THE POINT.
 *   - The identity block renders from the tree alone and NEVER depends on the
 *     network. With the API down the panel is still useful.
 *   - The KG block has exactly four terminal states: ready / empty /
 *     unavailable / timed-out. There is no state in which a spinner persists.
 *   - fetchNodeDetail (from useAssembly) already swallows every network error
 *     and resolves to an empty detail; the only failure it cannot cover is a
 *     request that never settles, so this component races it against a timer.
 *
 * Props: { nodeId, tree } — see the JSDoc on the component.
 *
 * Plain JS + JSX, no TypeScript, no new dependencies.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { displayName } from './assemblyTree.js';
import { useAssembly, DEFAULT_VEHICLE } from './useAssembly.js';
import './PartDetailPanel.css';

/** R5 — must be on screen wherever this geometry is presented. */
export const PROVENANCE_TEXT = 'Representative geometry — not OEM CAD';

/** Wait this long before admitting we are loading, so fast fetches never flash. */
const SPINNER_DELAY_MS = 220;
/** Soft warning: still in flight, tell the user rather than looking frozen. */
const SLOW_NOTICE_MS = 3500;
/** Hard ceiling. Past this we stop waiting and render the unavailable state. */
const REQUEST_TIMEOUT_MS = 9000;

const LEVEL_LABEL = {
  assembly: 'Assembly',
  subassembly: 'Sub-assembly',
  unit: 'Unit',
  unknown: 'Unclassified',
};

/**
 * The Excel ingest read header rows as data, so some FailureCase nodes carry
 * literal column titles as values (SPEC §8). Drop those rather than show them.
 */
const HEADER_NOISE = new Set([
  'failure mode', 'root cause', 'detection', 'name', 'component',
  'specification', 'value', 'parameter', 'procedure', 'system', 'n/a', 'na',
  '-', '--', 'none', 'null', 'nan', 'undefined',
]);

function clean(value) {
  if (value === null || value === undefined) return '';
  const s = String(value).trim();
  if (!s) return '';
  if (HEADER_NOISE.has(s.toLowerCase())) return '';
  return s;
}

const asArray = (v) => (Array.isArray(v) ? v : []);

/** Keep only failures that still say something after header-noise filtering. */
function usefulFailures(failures) {
  const out = [];
  for (const f of asArray(failures)) {
    if (!f || typeof f !== 'object') continue;
    const row = {
      name: clean(f.name),
      failure_mode: clean(f.failure_mode),
      root_cause: clean(f.root_cause),
      detection: clean(f.detection),
    };
    if (row.name || row.failure_mode || row.root_cause || row.detection) out.push(row);
  }
  return out;
}

function usefulSpecs(specs) {
  const out = [];
  for (const s of asArray(specs)) {
    if (!s || typeof s !== 'object') continue;
    const name = clean(s.name);
    const value = clean(s.value);
    if (name || value) out.push({ name: name || '—', value: value || '—' });
  }
  return out;
}

function usefulProcedures(procedures) {
  const out = [];
  for (const p of asArray(procedures)) {
    if (!p || typeof p !== 'object') continue;
    const name = clean(p.name);
    const text = clean(p.text);
    if (name || text) out.push({ name: name || 'Procedure', text });
  }
  return out;
}

function usefulEntities(matched) {
  const out = [];
  for (const m of asArray(matched)) {
    if (!m) continue;
    if (typeof m === 'string') {
      const s = clean(m);
      if (s) out.push({ id: s, name: s, label: '' });
      continue;
    }
    if (typeof m !== 'object') continue;
    const name = clean(m.name);
    const id = clean(m.id);
    if (!name && !id) continue;
    out.push({ id: id || name, name: name || id, label: clean(m.label) });
  }
  return out;
}

function usefulSystems(systems) {
  const seen = new Set();
  const out = [];
  for (const s of asArray(systems)) {
    const v = clean(typeof s === 'string' ? s : (s && s.name));
    if (!v) continue;
    const key = v.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v);
  }
  return out;
}

/** Resolve a node from the tree without assuming the tree is well-formed. */
function lookupNode(tree, nodeId) {
  if (!nodeId || !tree) return null;
  const byId = tree.byId;
  if (byId && typeof byId.get === 'function') return byId.get(nodeId) ?? null;
  if (Array.isArray(tree.nodes)) return tree.nodes.find((n) => n && n.id === nodeId) ?? null;
  return null;
}

/**
 * Breadcrumb from root to the node itself: Assembly > Sub-assembly > Unit.
 * Falls back to the id convention if the tree cannot answer.
 */
function buildPath(tree, nodeId) {
  const out = [];
  if (!nodeId) return out;
  const ancestors = (tree && typeof tree.ancestorsOf === 'function')
    ? tree.ancestorsOf(nodeId)
    : [];
  for (let i = ancestors.length - 1; i >= 0; i--) {
    const id = ancestors[i];
    const n = lookupNode(tree, id);
    out.push({ id, name: (n && n.name) || displayName(id), level: n && n.level });
  }
  const self = lookupNode(tree, nodeId);
  out.push({
    id: nodeId,
    name: (self && self.name) || displayName(nodeId),
    level: self && self.level,
    self: true,
  });
  return out;
}

/* ------------------------------------------------------------------ *
 * KG detail loading
 * ------------------------------------------------------------------ */

const PHASE_IDLE = 'idle';
const PHASE_LOADING = 'loading';
const PHASE_READY = 'ready';
const PHASE_UNAVAILABLE = 'unavailable';
const PHASE_TIMEOUT = 'timeout';

/**
 * Loads KG detail for a node with a delayed spinner, a slow-response notice and
 * a hard timeout. Never throws, never leaves the caller in `loading` forever.
 */
function useNodeDetail(nodeId, fetchNodeDetail, reloadToken) {
  const [state, setState] = useState({ phase: PHASE_IDLE, detail: null, slow: false });
  // Guards against out-of-order responses when the user clicks parts quickly.
  const reqSeq = useRef(0);

  useEffect(() => {
    const seq = ++reqSeq.current;

    if (!nodeId) {
      setState({ phase: PHASE_IDLE, detail: null, slow: false });
      return undefined;
    }
    if (typeof fetchNodeDetail !== 'function') {
      setState({ phase: PHASE_UNAVAILABLE, detail: null, slow: false });
      return undefined;
    }

    let settled = false;
    // Start optimistic: if the cache answers synchronously-ish we never show a
    // spinner at all. The spinner only appears once SPINNER_DELAY_MS elapses.
    setState({ phase: PHASE_IDLE, detail: null, slow: false });

    const spinnerTimer = setTimeout(() => {
      if (settled || reqSeq.current !== seq) return;
      setState({ phase: PHASE_LOADING, detail: null, slow: false });
    }, SPINNER_DELAY_MS);

    const slowTimer = setTimeout(() => {
      if (settled || reqSeq.current !== seq) return;
      setState({ phase: PHASE_LOADING, detail: null, slow: true });
    }, SLOW_NOTICE_MS);

    const timeoutTimer = setTimeout(() => {
      if (settled || reqSeq.current !== seq) return;
      settled = true;
      setState({ phase: PHASE_TIMEOUT, detail: null, slow: false });
    }, REQUEST_TIMEOUT_MS);

    Promise.resolve()
      .then(() => fetchNodeDetail(nodeId))
      .then((detail) => {
        if (settled || reqSeq.current !== seq) return;
        settled = true;
        if (!detail || typeof detail !== 'object') {
          setState({ phase: PHASE_UNAVAILABLE, detail: null, slow: false });
        } else if (detail.unavailable) {
          setState({ phase: PHASE_UNAVAILABLE, detail, slow: false });
        } else {
          setState({ phase: PHASE_READY, detail, slow: false });
        }
      })
      .catch(() => {
        // fetchNodeDetail is documented not to reject; belt and braces.
        if (settled || reqSeq.current !== seq) return;
        settled = true;
        setState({ phase: PHASE_UNAVAILABLE, detail: null, slow: false });
      });

    return () => {
      settled = true;
      clearTimeout(spinnerTimer);
      clearTimeout(slowTimer);
      clearTimeout(timeoutTimer);
    };
  }, [nodeId, fetchNodeDetail, reloadToken]);

  return state;
}

/* ------------------------------------------------------------------ *
 * Presentational bits
 * ------------------------------------------------------------------ */

function Section({ title, count, children }) {
  return (
    <section className="pdp-section">
      <h4 className="pdp-section__title">
        <span>{title}</span>
        {typeof count === 'number' && count > 0 && (
          <span className="pdp-section__count">{count}</span>
        )}
      </h4>
      {children}
    </section>
  );
}

function FailureCard({ failure, index }) {
  const rows = [
    ['Failure mode', failure.failure_mode],
    ['Root cause', failure.root_cause],
    ['Proposed detection', failure.detection],
  ].filter(([, v]) => v);

  return (
    <li className="pdp-failure">
      <div className="pdp-failure__head">
        <span className="pdp-failure__index">{String(index + 1).padStart(2, '0')}</span>
        <span className="pdp-failure__name">{failure.name || 'Unnamed failure case'}</span>
      </div>
      {rows.length > 0 ? (
        <dl className="pdp-failure__body">
          {rows.map(([label, value]) => (
            <div className="pdp-failure__row" key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="pdp-failure__thin">No further detail recorded for this case.</p>
      )}
    </li>
  );
}

function KgEmpty({ variant, onRetry }) {
  const copy = {
    empty: {
      icon: '○',
      title: 'No knowledge-graph data linked to this part yet',
      body: 'Geometry for this part exists, but nothing in the knowledge graph '
        + 'has been bound to it. Selecting a parent assembly may show related data.',
      retry: false,
    },
    unavailable: {
      icon: '⚠',
      title: 'Knowledge-graph service unavailable',
      body: 'Geometry and the assembly tree are unaffected — this panel simply '
        + 'has no enrichment to show right now.',
      retry: true,
    },
    timeout: {
      icon: '⧗',
      title: 'Knowledge-graph service did not respond in time',
      body: 'The request was stopped after '
        + `${Math.round(REQUEST_TIMEOUT_MS / 1000)} seconds so the panel stays responsive.`,
      retry: true,
    },
  }[variant] ?? {
    icon: '○', title: 'No data', body: '', retry: false,
  };

  return (
    <div className={`pdp-empty pdp-empty--${variant}`} role="status">
      <span className="pdp-empty__icon" aria-hidden="true">{copy.icon}</span>
      <p className="pdp-empty__title">{copy.title}</p>
      {copy.body && <p className="pdp-empty__body">{copy.body}</p>}
      {copy.retry && typeof onRetry === 'function' && (
        <button type="button" className="pdp-empty__retry" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

function KgLoading({ slow }) {
  return (
    <div className="pdp-loading" role="status" aria-live="polite">
      <div className="pdp-skeleton pdp-skeleton--wide" />
      <div className="pdp-skeleton" />
      <div className="pdp-skeleton pdp-skeleton--short" />
      <p className="pdp-loading__label">
        {slow
          ? 'Still waiting on the knowledge graph…'
          : 'Loading knowledge-graph data…'}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

/**
 * @param {object} props
 * @param {string|null} [props.nodeId]   Selected node id, e.g. "U3_1_3_CELL_MODULE_04".
 *                                       null/undefined renders the "nothing selected" state.
 * @param {object} [props.tree]          Tree from useAssembly(); needs byId / ancestorsOf.
 * @param {string} [props.vehicle]       Vehicle key for the KG lookup. Default "ace_ev".
 * @param {(id:string)=>Promise<object>} [props.fetchNodeDetail]
 *                                       Optional injection; defaults to useAssembly's.
 * @param {() => void} [props.onClose]   Optional; renders a close button when supplied.
 * @param {string} [props.className]     Optional extra class on the root element.
 */
function PartDetailPanelImpl({
  nodeId = null,
  tree = null,
  vehicle = DEFAULT_VEHICLE,
  fetchNodeDetail: fetchNodeDetailProp,
  onClose,
  className = '',
}) {
  // Hooks must run unconditionally; the hook's own fetcher is only used when
  // the caller has not injected one. The manifest it loads is a cached static
  // asset, so the duplicate request is free after the first paint.
  const assembly = useAssembly({ vehicle });
  const fetchNodeDetail = fetchNodeDetailProp ?? assembly.fetchNodeDetail;

  const [reloadToken, setReloadToken] = useState(0);
  const retry = useCallback(() => setReloadToken((t) => t + 1), []);

  const { phase, detail, slow } = useNodeDetail(nodeId, fetchNodeDetail, reloadToken);

  const node = useMemo(() => lookupNode(tree, nodeId), [tree, nodeId]);
  const path = useMemo(() => buildPath(tree, nodeId), [tree, nodeId]);

  const kg = useMemo(() => {
    if (phase !== PHASE_READY || !detail) {
      return { entities: [], specs: [], systems: [], failures: [], procedures: [], total: 0 };
    }
    const entities = usefulEntities(detail.matched_kg);
    const specs = usefulSpecs(detail.specifications);
    const systems = usefulSystems(detail.systems);
    const failures = usefulFailures(detail.failures);
    const procedures = usefulProcedures(detail.procedures);
    return {
      entities,
      specs,
      systems,
      failures,
      procedures,
      total: entities.length + specs.length + systems.length
        + failures.length + procedures.length,
    };
  }, [phase, detail]);

  const provenance = (
    <div className="pdp-provenance" title="SPEC R5">
      <span className="pdp-provenance__dot" aria-hidden="true" />
      {PROVENANCE_TEXT}
    </div>
  );

  /* ---- nothing selected ------------------------------------------- */
  if (!nodeId) {
    return (
      <aside className={`pdp ${className}`.trim()} aria-label="Part detail">
        <div className="pdp-empty pdp-empty--idle" role="status">
          <span className="pdp-empty__icon" aria-hidden="true">◎</span>
          <p className="pdp-empty__title">No part selected</p>
          <p className="pdp-empty__body">
            Pick a part in the 3D view or the assembly tree to see its
            specifications and known failure modes.
          </p>
        </div>
        {provenance}
      </aside>
    );
  }

  const name = (node && node.name) || displayName(nodeId);
  const level = LEVEL_LABEL[(node && node.level) || 'unknown'] || 'Unclassified';
  const unknownToTree = !node;

  return (
    <aside className={`pdp ${className}`.trim()} aria-label={`Part detail: ${name}`}>
      <header className="pdp-header">
        <div className="pdp-header__main">
          <span className="pdp-header__level">{level}</span>
          <h3 className="pdp-header__name">{name}</h3>
          <code className="pdp-header__id">{nodeId}</code>
        </div>
        {typeof onClose === 'function' && (
          <button
            type="button"
            className="pdp-header__close"
            onClick={onClose}
            aria-label="Close part detail"
          >
            ×
          </button>
        )}
      </header>

      <nav className="pdp-path" aria-label="Assembly path">
        {path.map((step, i) => (
          <span key={step.id} className="pdp-path__step">
            {i > 0 && <span className="pdp-path__sep" aria-hidden="true">›</span>}
            <span className={`pdp-path__name${step.self ? ' is-self' : ''}`}>
              {step.name}
            </span>
          </span>
        ))}
      </nav>

      {unknownToTree && (
        <p className="pdp-note">
          This id is not in the loaded assembly manifest; showing what can be
          derived from the id alone.
        </p>
      )}

      <div className="pdp-body">
        {/* PHASE_IDLE with a nodeId is the sub-spinner-delay window: render a
            reserved blank so a fast/cached answer never flashes a skeleton. */}
        {phase === PHASE_IDLE && <div className="pdp-prehold" aria-hidden="true" />}
        {phase === PHASE_LOADING && <KgLoading slow={slow} />}

        {phase === PHASE_UNAVAILABLE && <KgEmpty variant="unavailable" onRetry={retry} />}
        {phase === PHASE_TIMEOUT && <KgEmpty variant="timeout" onRetry={retry} />}

        {phase === PHASE_READY && kg.total === 0 && <KgEmpty variant="empty" />}

        {phase === PHASE_READY && kg.total > 0 && (
          <>
            {kg.systems.length > 0 && (
              <Section title="Systems" count={kg.systems.length}>
                <ul className="pdp-chips">
                  {kg.systems.map((s) => (
                    <li className="pdp-chip" key={s}>{s}</li>
                  ))}
                </ul>
              </Section>
            )}

            {kg.specs.length > 0 && (
              <Section title="Specifications" count={kg.specs.length}>
                <dl className="pdp-specs">
                  {kg.specs.map((s, i) => (
                    <div className="pdp-specs__row" key={`${s.name}-${i}`}>
                      <dt>{s.name}</dt>
                      <dd>{s.value}</dd>
                    </div>
                  ))}
                </dl>
              </Section>
            )}

            {kg.failures.length > 0 && (
              <Section title="Failure modes" count={kg.failures.length}>
                <ul className="pdp-failures">
                  {kg.failures.map((f, i) => (
                    <FailureCard key={`${f.name}-${i}`} failure={f} index={i} />
                  ))}
                </ul>
              </Section>
            )}

            {kg.procedures.length > 0 && (
              <Section title="Procedures" count={kg.procedures.length}>
                <ul className="pdp-procedures">
                  {kg.procedures.map((p, i) => (
                    <li className="pdp-procedure" key={`${p.name}-${i}`}>
                      <span className="pdp-procedure__name">{p.name}</span>
                      {p.text && <p className="pdp-procedure__text">{p.text}</p>}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {kg.entities.length > 0 && (
              <Section title="Matched knowledge-graph entities" count={kg.entities.length}>
                <ul className="pdp-entities">
                  {kg.entities.map((e, i) => (
                    <li className="pdp-entity" key={`${e.id}-${i}`}>
                      <span className="pdp-entity__name">{e.name}</span>
                      {e.label && <span className="pdp-entity__label">{e.label}</span>}
                      <code className="pdp-entity__id">{e.id}</code>
                    </li>
                  ))}
                </ul>
              </Section>
            )}
          </>
        )}
      </div>

      {provenance}
    </aside>
  );
}

/**
 * Memoised for the same reason as AssemblyTree: the explode slider re-renders
 * ExplodedView up to ~100 times per drag, and none of this panel's props change
 * on a `t` change.
 */
export const PartDetailPanel = memo(PartDetailPanelImpl);
PartDetailPanel.displayName = 'PartDetailPanel';

export default PartDetailPanel;
